import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  MdAccessTime,
  MdEvent,
  MdMeetingRoom,
  MdTrendingUp,
  MdCalendarToday,
  MdScience,
  MdWarning,
  MdOpenInNew,
  MdShoppingCart,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import theme, { conditionColor, conditionBg, tierConfig, getCoverageAreaColor } from './theme';
import api from '../utils/api';
import { getClientEmail } from '../utils/getClientEmail';
import { buildBriefingBulletParts, buildFallbackBriefingParts } from './clientBriefingFromSessions';
import { efficacyParamsFromHospital } from './efficacyRequestParams';
import {
  currentYYYYMM,
  addMonthsYYYYMM,
  formatYYYYMMLong,
  chartYearMonthBounds,
  hoursChartRowYYYYMM,
  findHoursChartRow,
} from './overviewMonthUtils';
import { buildOverviewFallbackFromOperations } from './overviewLegacyMonthPatch';
import DashboardInsightCard from './DashboardInsightCard';

function getClientTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function mergeOverviewPayload(overviewBody, opsBody, selectedMonth) {
  if (!overviewBody?.success) return null;
  const base = { ...overviewBody };
  if (base.appliedCalcMonth) return base;
  const patch = buildOverviewFallbackFromOperations(opsBody, base, selectedMonth);
  if (!patch) return base;
  return {
    ...base,
    ...patch,
    hoursChart: base.hoursChart,
    currentPlan: base.currentPlan,
    executiveSummaryFromView: base.executiveSummaryFromView,
    executiveSummary: base.executiveSummary,
  };
}

function activityRoomLabel(s) {
  const fromName = s?.roomName != null ? String(s.roomName).trim() : '';
  if (fromName) return fromName;
  const fromRoom = s?.room != null ? String(s.room).trim() : '';
  if (fromRoom && fromRoom !== '—') return fromRoom;
  return '';
}

function renderBriefingParts(parts) {
  return parts.map((p, j) => {
    if (typeof p === 'string') return <React.Fragment key={j}>{p}</React.Fragment>;
    if (p && typeof p === 'object' && p.bold != null) {
      return (
        <Box component="span" key={j} sx={{ fontWeight: 700, color: theme.colors.textPrimary }}>
          {p.bold}
        </Box>
      );
    }
    return null;
  });
}

function KpiCard({ icon, label, value, subtext, accent }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: `${theme.radius.lg}px`,
        bgcolor: theme.colors.bgSecondary,
        border: `1px solid ${theme.colors.borderDefault}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: accent || theme.colors.brandPrimary, display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {subtext && (
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>{subtext}</Typography>
      )}
    </Paper>
  );
}

function getBriefingDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 90);
  return {
    startStr: start.toISOString().slice(0, 10),
    endStr: end.toISOString().slice(0, 10),
  };
}

/** Set true to re-enable Monthly Briefing card on Overview. */
const SHOW_MONTHLY_BRIEFING = false;

/** Set true to re-enable Attention Needed (overdue rooms) banner on Overview. */
const SHOW_ATTENTION_NEEDED = false;

const MONTHLY_BRIEFING_POINT_COUNT = 8;

function splitBriefingNarrativeToPoints(text, max) {
  const t = String(text || '').trim();
  if (!t) return [];
  const sentences = t.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  if (sentences.length) return sentences.slice(0, max);
  return [t].slice(0, max);
}

function padMonthlyNarrativeLines(lines, kpis) {
  const out = [...lines];
  const fillers = [
    kpis?.hoursThisMonth != null && `UVC this month: ${kpis.hoursThisMonth}h`,
    kpis?.sessions != null && `Sessions this month: ${kpis.sessions}`,
    kpis?.roomsCovered != null && `Rooms covered: ${kpis.roomsCovered}`,
    kpis?.lastService != null && `Last service: ${kpis.lastService}`,
  ].filter(Boolean);
  let i = 0;
  while (out.length < MONTHLY_BRIEFING_POINT_COUNT && i < fillers.length) out.push(fillers[i++]);
  while (out.length < MONTHLY_BRIEFING_POINT_COUNT) out.push('—');
  return out.slice(0, MONTHLY_BRIEFING_POINT_COUNT);
}

/** e.g. "4 sessions (12%)" or "1 session (12%)" */
function formatCoverageSessionLabel(count, valuePct) {
  if (!count) return `${valuePct}%`;
  const noun = count === 1 ? 'session' : 'sessions';
  return `${count} ${noun} (${valuePct}%)`;
}

/** Donut uses share %; list shows disinfection count with % in brackets. */
function buildCoverageLayout(coverageData, totalSessions = null) {
  const raw = Array.isArray(coverageData) ? coverageData : [];
  const cleaned = raw
    .map((r) => {
      const name = r?.name != null ? String(r.name).trim() : '';
      const countRaw = Number(r?.count);
      const count =
        Number.isFinite(countRaw) && countRaw > 0
          ? Math.round(countRaw)
          : null;
      const value = Math.max(0, Number(r?.value) || 0);
      return { name, count, value };
    })
    .filter((r) => r.name && (r.count > 0 || r.value > 0));
  if (!cleaned.length) {
    return { hasRows: false, pieRows: [], listRows: [], areaCount: 0 };
  }
  const mergedByName = new Map();
  for (const r of cleaned) {
    const prev = mergedByName.get(r.name) || { count: 0, value: 0 };
    mergedByName.set(r.name, {
      count: prev.count + (r.count ?? 0),
      value: Math.max(prev.value, r.value),
    });
  }
  let rows = [...mergedByName.entries()].map(([name, { count, value }]) => ({ name, count, value }));
  const sessionsTotal = Number.isFinite(Number(totalSessions)) ? Math.max(0, Number(totalSessions)) : 0;
  if (rows.some((r) => !r.count) && sessionsTotal > 0) {
    rows = rows.map((r) =>
      r.count
        ? r
        : { ...r, count: Math.max(1, Math.round((r.value / 100) * sessionsTotal)) }
    );
  }
  const totalCount = rows.reduce((s, r) => s + (r.count || 0), 0) || 1;
  rows = rows.map((r) =>
    r.count
      ? { name: r.name, count: r.count, value: Math.round((r.count / totalCount) * 100) }
      : { name: r.name, count: 0, value: r.value }
  );
  const sorted = rows.filter((r) => r.count > 0 || r.value > 0).sort((a, b) => b.count - a.count || b.value - a.value);
  return {
    hasRows: sorted.length > 0,
    pieRows: sorted,
    listRows: sorted,
    areaCount: sorted.length,
  };
}

function HoursChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Paper
      elevation={2}
      sx={{
        p: 1.5,
        borderRadius: `${theme.radius.md}px`,
        border: `1px solid ${theme.colors.borderDefault}`,
        bgcolor: theme.colors.bgPrimary,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.5 }}>
        {row.month}
      </Typography>
      <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>Hours: {row.hours ?? '—'}</Typography>
      {row.sessions != null ? (
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>Sessions: {row.sessions}</Typography>
      ) : null}
      {row.roomsCovered != null ? (
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>Rooms covered: {row.roomsCovered}</Typography>
      ) : null}
    </Paper>
  );
}

export default function OverviewPage() {
  const { hasEfficacy, hospitalInfo, clientPurchaseTypeNorm, clientEmail } = useOutletContext();
  const isPurchaseUser = String(clientPurchaseTypeNorm ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '') === 'purchaseuser';
  const [selectedMonth, setSelectedMonth] = useState(() => currentYYYYMM());
  const [chartData, setChartData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [briefingSessions, setBriefingSessions] = useState(null);
  const [efficacyOverall, setEfficacyOverall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const fetchRequestId = useRef(0);
  const chartFetchStarted = useRef(false);

  const fetchChart = useCallback(async () => {
    const email = clientEmail || getClientEmail();
    if (!email) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/client/overview', { params: { email, includeChart: true } });
      if (res.data?.success) {
        setChartData({
          hoursChart: res.data.hoursChart || [],
          currentPlan: res.data.currentPlan || {},
        });
      }
    } catch (e) {
      console.error('Overview chart fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [clientEmail]);

  const fetchMonthDetail = useCallback(
    async (monthYm) => {
      const email = clientEmail || getClientEmail();
      if (!email) {
        setBriefingSessions([]);
        setMonthLoading(false);
        return;
      }

      const requestId = ++fetchRequestId.current;
      setMonthLoading(true);

      const tz = getClientTimezone();
      const { startStr: briefStart, endStr: briefEnd } = getBriefingDateRange();
      const effBase = hasEfficacy ? efficacyParamsFromHospital(hospitalInfo, email) : null;
      const effParams =
        effBase != null ? { ...effBase, calcMonth: monthYm, tz } : null;
      const efficacyPromise =
        effParams != null
          ? api.get('/client/efficacy', { params: effParams }).catch(() => ({ data: {} }))
          : Promise.resolve({ data: {} });

      try {
        const [overviewRes, opsRes, briefOpsRes, efficacyRes] = await Promise.all([
          api.get('/client/overview', {
            params: { email, calcMonth: monthYm, includeChart: false, tz },
          }),
          api
            .get('/client/operations', {
              params: {
                email,
                includeChart: false,
                granularity: 'monthly',
                periodKey: monthYm,
                calcMonth: monthYm,
                tz,
              },
            })
            .catch(() => ({ data: {} })),
          api
            .get('/client/operations', {
              params: {
                email,
                includeChart: false,
                startDate: briefStart,
                endDate: briefEnd,
              },
            })
            .catch(() => ({ data: {} })),
          efficacyPromise,
        ]);

        if (requestId !== fetchRequestId.current) return;

        const merged = mergeOverviewPayload(overviewRes.data, opsRes.data, monthYm);
        if (merged) setMonthData(merged);

        if (briefOpsRes.data?.success && Array.isArray(briefOpsRes.data.sessionLog)) {
          setBriefingSessions(briefOpsRes.data.sessionLog);
        } else if (opsRes.data?.success && Array.isArray(opsRes.data.sessionLog)) {
          setBriefingSessions(opsRes.data.sessionLog);
        } else {
          const embedded = overviewRes.data?.sessionLog ?? overviewRes.data?.sessions;
          setBriefingSessions(Array.isArray(embedded) ? embedded : []);
        }

        if (efficacyRes.data?.success && efficacyRes.data.overallEfficacy != null) {
          setEfficacyOverall(Number(efficacyRes.data.overallEfficacy));
        } else {
          setEfficacyOverall(null);
        }
      } catch (e) {
        console.error('Overview month detail fetch error:', e);
      } finally {
        if (requestId === fetchRequestId.current) {
          setMonthLoading(false);
        }
      }
    },
    [hasEfficacy, hospitalInfo, clientEmail]
  );

  useEffect(() => {
    if (chartFetchStarted.current) return;
    chartFetchStarted.current = true;
    fetchChart();
  }, [fetchChart]);

  useEffect(() => {
    fetchMonthDetail(selectedMonth);
  }, [selectedMonth, fetchMonthDetail]);

  const hoursChart = useMemo(() => chartData?.hoursChart || [], [chartData?.hoursChart]);
  const chartBounds = useMemo(() => chartYearMonthBounds(hoursChart), [hoursChart]);
  const selectedChartRow = useMemo(
    () => findHoursChartRow(hoursChart, selectedMonth),
    [hoursChart, selectedMonth]
  );
  const kpis = useMemo(() => {
    const fromMonth = monthData?.kpis || {};
    if (monthLoading && selectedChartRow) {
      return {
        ...fromMonth,
        hoursThisMonth: fromMonth.hoursThisMonth ?? selectedChartRow.hours,
        sessions: fromMonth.sessions ?? selectedChartRow.sessions,
        roomsCovered: fromMonth.roomsCovered ?? selectedChartRow.roomsCovered,
      };
    }
    return fromMonth;
  }, [monthData?.kpis, monthLoading, selectedChartRow]);
  const briefing = monthData?.briefing || {};
  const canGoBack = chartBounds?.min ? selectedMonth > chartBounds.min : true;
  const canGoForward = selectedMonth < currentYYYYMM();
  const selectedMonthLabel = formatYYYYMMLong(selectedMonth);
  const prevMonthLabel = useMemo(() => {
    const prev = addMonthsYYYYMM(selectedMonth, -1);
    if (chartBounds?.min && prev < chartBounds.min) return null;
    return formatYYYYMMLong(prev);
  }, [selectedMonth, chartBounds]);
  const nextMonthLabel = useMemo(() => {
    const next = addMonthsYYYYMM(selectedMonth, 1);
    if (next > currentYYYYMM()) return null;
    return formatYYYYMMLong(next);
  }, [selectedMonth]);
  const monthSectionsBusy = monthLoading && !loading;
  const coverageData = useMemo(() => {
    const raw = monthData?.coverage || [];
    return raw.filter((r) => String(r?.name ?? '').trim() !== 'Other');
  }, [monthData]);
  const coverageLayout = useMemo(
    () => buildCoverageLayout(coverageData, kpis?.sessions),
    [coverageData, kpis?.sessions]
  );
  const coverageBarMax = useMemo(() => {
    const list = coverageLayout.listRows;
    if (!list.length) return 1;
    return Math.max(1, ...list.map((r) => r.count || 0));
  }, [coverageLayout.listRows]);
  const recentActivity = useMemo(() => {
    const list = Array.isArray(monthData?.recentActivity) ? monthData.recentActivity : [];
    return list.filter((s) => activityRoomLabel(s) !== '');
  }, [monthData?.recentActivity]);
  const currentPlan = chartData?.currentPlan || {};
  const overdueRooms = useMemo(() => monthData?.overdueRooms || [], [monthData]);
  const overdueRoomsAttention = useMemo(() => {
    const list = Array.isArray(overdueRooms) ? [...overdueRooms] : [];
    list.sort((a, b) => {
      const da = Number(a?.daysSince);
      const db = Number(b?.daysSince);
      const aOk = Number.isFinite(da);
      const bOk = Number.isFinite(db);
      if (aOk && bOk) return da - db;
      if (aOk) return -1;
      if (bOk) return 1;
      return 0;
    });
    return list.slice(0, 6);
  }, [overdueRooms]);
  const tierCeiling = isPurchaseUser ? null : tierConfig[currentPlan.tier]?.hours || 36;
  const includedHoursFromKpi = useMemo(() => {
    if (isPurchaseUser) return null;
    const includedFromPlan = Number(currentPlan?.includedHours);
    if (Number.isFinite(includedFromPlan) && includedFromPlan > 0) return includedFromPlan;
    const hours = Number(kpis?.hoursThisMonth);
    const pct = Number(kpis?.tierUsagePct);
    if (Number.isFinite(hours) && Number.isFinite(pct) && pct > 0) {
      const derived = Math.round((hours / pct) * 100);
      if (Number.isFinite(derived) && derived > 0) return derived;
    }
    return null;
  }, [isPurchaseUser, currentPlan?.includedHours, kpis?.hoursThisMonth, kpis?.tierUsagePct]);
  const currentPlanHoursUsed = Number.isFinite(Number(kpis?.hoursThisMonth)) ? Number(kpis.hoursThisMonth) : 0;
  const currentPlanHoursCeiling = Number.isFinite(Number(includedHoursFromKpi))
    ? Number(includedHoursFromKpi)
    : null;
  const currentPlanUsageRatio =
    currentPlanHoursCeiling && currentPlanHoursCeiling > 0
      ? currentPlanHoursUsed / currentPlanHoursCeiling
      : 0;

  const executiveSummaryFromView = useMemo(() => {
    const fromApi = monthData?.executiveSummaryFromView;
    if (fromApi && (fromApi.points?.length || fromApi.narrative)) return fromApi;
    const highlights = Array.isArray(briefing?.highlights) ? briefing.highlights : [];
    const fallbackPoints = highlights
      .map((h) => h?.text)
      .filter((t) => typeof t === 'string' && t.trim())
      .map((text) => `<b>Highlight:</b> ${text.trim()}`);
    if (fallbackPoints.length) {
      return { points: fallbackPoints, fromCache: false, source: 'template', error: fromApi?.error };
    }
    if (briefing?.narrative) {
      return { narrative: briefing.narrative, points: [], fromCache: false, source: 'template' };
    }
    return fromApi ?? null;
  }, [monthData?.executiveSummaryFromView, briefing.highlights, briefing.narrative]);

  const briefingBulletModel = useMemo(() => {
    const sessions = Array.isArray(briefingSessions) ? briefingSessions : [];
    if (sessions.length > 0) {
      return { kind: 'parts', ...buildBriefingBulletParts(sessions, { windowDays: 90, coverageAreas: coverageData }) };
    }
    if (briefing.narrative && String(briefing.narrative).trim()) {
      return { kind: 'narrative', text: briefing.narrative };
    }
    return { kind: 'parts', ...buildFallbackBriefingParts(kpis, coverageData) };
  }, [briefingSessions, briefing.narrative, coverageData, kpis]);

  const monthlyNarrativePoints = useMemo(() => {
    if (briefingBulletModel.kind !== 'narrative' || !briefingBulletModel.text) return [];
    const base = splitBriefingNarrativeToPoints(briefingBulletModel.text, MONTHLY_BRIEFING_POINT_COUNT);
    return padMonthlyNarrativeLines(base, kpis);
  }, [briefingBulletModel.kind, briefingBulletModel.text, kpis]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* UVC Hours — multi-month trend (first section) */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: `${theme.radius.lg}px`, border: `1px solid ${theme.colors.borderDefault}` }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 2 }}>UVC Hours</Typography>
            <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mb: 1.5 }}>
              Tap a bar to view that month below
            </Typography>
            <ResponsiveContainer width="100%" height={hoursChart.length > 12 ? 320 : 280}>
              <BarChart
                data={hoursChart}
                margin={{ top: 8, right: 8, left: 0, bottom: hoursChart.length > 12 ? 28 : 0 }}
                onClick={(state) => {
                  const payload = state?.activePayload?.[0]?.payload;
                  const ym = hoursChartRowYYYYMM(payload);
                  if (ym) setSelectedMonth(ym);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderDefault} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: theme.colors.textSecondary }}
                  interval={hoursChart.length > 12 ? 'preserveStartEnd' : 0}
                />
                <YAxis tick={{ fontSize: 12, fill: theme.colors.textSecondary }} />
                <Tooltip content={<HoursChartTooltip />} />
                {tierCeiling != null && tierCeiling > 0 ? (
                  <ReferenceLine
                    y={tierCeiling}
                    stroke={theme.colors.accentWarning}
                    strokeDasharray="8 4"
                    label={{
                      value: `${currentPlan.tier || 'Tier'} ceiling`,
                      position: 'right',
                      fontSize: 11,
                      fill: theme.colors.accentWarning,
                    }}
                  />
                ) : null}
                <Bar dataKey="hours" radius={[4, 4, 0, 0]} name="Hours" cursor="pointer">
                  {hoursChart.map((entry, index) => {
                    const ym = hoursChartRowYYYYMM(entry);
                    const active = ym === selectedMonth;
                    return (
                      <Cell
                        key={`uvc-${index}`}
                        fill={active ? theme.colors.brandPrimary : `${theme.colors.brandPrimary}99`}
                        stroke={active ? '#0f172a' : 'none'}
                        strokeWidth={active ? 3 : 0}
                        strokeDasharray={active ? '6 2' : undefined}
                      />
                    );
                  })}
                </Bar>
                {hoursChart.length > 12 ? (
                  <Brush
                    dataKey="month"
                    height={24}
                    stroke={theme.colors.brandPrimary}
                    travellerWidth={8}
                  />
                ) : null}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Month navigator — drives sections below */}
      <Box
        role="navigation"
        aria-label="Month navigator"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
          p: 2,
          borderRadius: `${theme.radius.lg}px`,
          border: `1px solid ${theme.colors.borderDefault}`,
          bgcolor: theme.colors.bgSecondary,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.colors.textSecondary, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Viewing month
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.2 }}>
            {selectedMonthLabel}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.25 }}>
            Use the arrows to view another month — all sections below update.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MdChevronLeft size={18} />}
            disabled={!canGoBack || monthSectionsBusy}
            onClick={() =>
              setSelectedMonth((m) => {
                const next = addMonthsYYYYMM(m, -1);
                if (chartBounds?.min && next < chartBounds.min) return chartBounds.min;
                return next;
              })
            }
            aria-label={prevMonthLabel ? `Previous month: ${prevMonthLabel}` : 'Previous month'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.colors.borderDefault,
              color: theme.colors.textPrimary,
              bgcolor: theme.colors.bgPrimary,
              '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {prevMonthLabel || 'Previous'}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Prev
            </Box>
          </Button>
          <Button
            size="small"
            variant="outlined"
            endIcon={<MdChevronRight size={18} />}
            disabled={!canGoForward || monthSectionsBusy}
            onClick={() => setSelectedMonth((m) => addMonthsYYYYMM(m, 1))}
            aria-label={nextMonthLabel ? `Next month: ${nextMonthLabel}` : 'Next month'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.colors.borderDefault,
              color: theme.colors.textPrimary,
              bgcolor: theme.colors.bgPrimary,
              '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {nextMonthLabel || 'Next'}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Next
            </Box>
          </Button>
          {monthSectionsBusy && <CircularProgress size={18} sx={{ color: theme.colors.brandPrimary, ml: 0.5 }} />}
        </Box>
      </Box>

      <Box sx={{ position: 'relative', opacity: monthSectionsBusy ? 0.55 : 1, transition: 'opacity 0.2s', pointerEvents: monthSectionsBusy ? 'none' : 'auto' }}>
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard icon={<MdAccessTime size={18} />} label="This month" value={`${kpis.hoursThisMonth ?? '—'}h`} subtext="UVC hours delivered" accent={theme.colors.brandPrimary} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard icon={<MdEvent size={18} />} label="Sessions" value={kpis.sessions ?? '—'} subtext="This month" accent={theme.colors.accentInfo} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard icon={<MdMeetingRoom size={18} />} label="Rooms covered" value={kpis.roomsCovered ?? '—'} subtext="Unique rooms" accent={theme.colors.accentSuccess} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {isPurchaseUser ? (
            <KpiCard
              icon={<MdShoppingCart size={18} />}
              label="Account"
              value="Purchase"
              subtext="Equipment purchase (no plan)"
              accent={theme.colors.textSecondary}
            />
          ) : (
            <KpiCard
              icon={<MdTrendingUp size={18} />}
              label="Tier usage"
              value={`${kpis.tierUsagePct ?? '—'}%`}
              subtext={`${currentPlan.tier || '—'} tier`}
              accent={theme.colors.accentWarning}
            />
          )}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard icon={<MdCalendarToday size={18} />} label="Last service" value={kpis.lastService ?? '—'} subtext="Most recent" accent={theme.colors.textSecondary} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {hasEfficacy ? (
            <KpiCard
              icon={<MdScience size={18} />}
              label="Efficacy"
              value={`${efficacyOverall ?? kpis.efficacyPct ?? '—'}%`}
              subtext="Overall efficacy"
              accent={theme.colors.accentSuccess}
            />
          ) : (
            <KpiCard icon={<MdMeetingRoom size={18} />} label="Active rooms" value={kpis.activeRoomsProtected ?? '—'} subtext="Protected (30d)" accent={theme.colors.accentSuccess} />
          )}
        </Grid>
      </Grid>

      {SHOW_MONTHLY_BRIEFING && (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: `${theme.radius.lg}px`,
          border: `1px solid ${theme.colors.borderDefault}`,
          bgcolor: theme.colors.bgPrimary,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.5 }}>Monthly Briefing</Typography>
            <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>{briefing.month || 'Current month'}</Typography>
          </Box>
          {briefing.reportUrl && (
            <Button
              size="small"
              endIcon={<MdOpenInNew size={14} />}
              onClick={() => window.open(briefing.reportUrl, '_blank')}
              sx={{
                background: theme.gradients.primary,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: `${theme.radius.md}px`,
                px: 2,
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.35)',
                '&:hover': {
                  background: theme.gradients.primaryHover,
                  boxShadow: '0 8px 25px rgba(236, 72, 153, 0.35)',
                },
              }}
            >
              Read full report
            </Button>
          )}
        </Box>
        {briefingBulletModel.kind === 'narrative' ? (
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.5,
              fontSize: 14,
              color: theme.colors.textPrimary,
              lineHeight: 1.7,
              '& li': { mb: 0.75 },
            }}
          >
            {monthlyNarrativePoints.map((line, i) => (
              <Box key={i} component="li" sx={{ color: theme.colors.textPrimary }}>
                {line}
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.5,
              fontSize: 14,
              color: theme.colors.textPrimary,
              lineHeight: 1.7,
              '& li': { mb: 0.75 },
            }}
          >
            {(briefingBulletModel.bullets || []).slice(0, MONTHLY_BRIEFING_POINT_COUNT).map((row) => (
              <Box key={row.key} component="li" sx={{ color: theme.colors.textPrimary }}>
                {renderBriefingParts(row.parts)}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      )}

      <DashboardInsightCard
        title="Executive Summary"
        subtitle={selectedMonthLabel}
        insightsFromView={executiveSummaryFromView}
        source={executiveSummaryFromView?.source ?? 'template'}
        loading={monthSectionsBusy}
        showFreshnessChip
        adminCacheInvalidation={{
          surface: 'overview',
          cacheParams: {
            email: clientEmail || getClientEmail() || '',
            calcMonth: selectedMonth,
            tz: getClientTimezone(),
          },
          onInvalidated: () => fetchMonthDetail(selectedMonth),
        }}
      />

      {/* Coverage by area */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: `${theme.radius.lg}px`,
              border: `1px solid ${theme.colors.borderDefault}`,
              minHeight: 0,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.5 }}>Coverage by area</Typography>
            <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mb: 2 }}>{selectedMonthLabel}</Typography>
            {coverageLayout.hasRows ? (
              <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: { xs: 360, md: 400 },
                      height: { xs: 260, sm: 300, md: 340 },
                      flexShrink: 0,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={coverageLayout.pieRows}
                          cx="50%"
                          cy="50%"
                          innerRadius={72}
                          outerRadius={118}
                          paddingAngle={0.5}
                          dataKey="value"
                          nameKey="name"
                        >
                          {coverageLayout.pieRows.map((entry, i) => (
                            <Cell
                              key={`${entry.name}-${i}`}
                              fill={getCoverageAreaColor(entry.name, coverageLayout.listRows)}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, item) => {
                            const count = item?.payload?.count;
                            const label =
                              count != null
                                ? formatCoverageSessionLabel(count, value)
                                : `${value}%`;
                            return [label, String(name ?? '')];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: theme.colors.textSecondary,
                      letterSpacing: '0.06em',
                      mb: 1,
                    }}
                  >
                    ALL AREAS
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: { xs: 200, md: 340 },
                      maxHeight: { xs: 'min(55vh, 480px)', md: 340 },
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      pr: 0.75,
                      mr: -0.25,
                      scrollbarGutter: 'stable',
                      scrollbarWidth: 'thin',
                      WebkitOverflowScrolling: 'touch',
                      border: `1px solid ${theme.colors.borderDefault}`,
                      borderRadius: `${theme.radius.md}px`,
                      bgcolor: theme.colors.bgSecondary,
                      px: 1.25,
                      py: 0.75,
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: 4,
                        bgcolor: 'rgba(15, 23, 42, 0.22)',
                      },
                      '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'rgba(15, 23, 42, 0.32)' },
                    }}
                  >
                    {coverageLayout.listRows.map((row, i) => {
                      const barPct = Math.min(100, ((row.count || 0) / coverageBarMax) * 100);
                        const fill = getCoverageAreaColor(row.name, coverageLayout.listRows);
                        return (
                          <Box
                            key={`${row.name}-${i}`}
                            sx={{
                              py: 1.1,
                              borderBottom:
                                i < coverageLayout.listRows.length - 1 ? `1px solid ${theme.colors.borderDefault}` : 'none',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                              <Box
                                component="span"
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '3px',
                                  bgcolor: fill,
                                  flexShrink: 0,
                                  mt: 0.45,
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: theme.colors.textPrimary,
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  {row.name}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    mt: 0.65,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <LinearProgress
                                    variant="determinate"
                                    value={barPct}
                                    sx={{
                                      flex: 1,
                                      minWidth: 80,
                                      height: 4,
                                      borderRadius: 2,
                                      bgcolor: theme.colors.bgPrimary,
                                      '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: fill },
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: theme.colors.textPrimary,
                                      fontVariantNumeric: 'tabular-nums',
                                      flexShrink: 0,
                                      minWidth: 96,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {formatCoverageSessionLabel(row.count, row.value)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, py: 2 }}>No coverage data for this period.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: `${theme.radius.lg}px`, border: `1px solid ${theme.colors.borderDefault}` }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 2 }}>Recent Activity</Typography>
            {recentActivity.length === 0 ? (
              <Typography sx={{ color: theme.colors.textSecondary, fontSize: 14 }}>No recent sessions.</Typography>
            ) : (
              recentActivity.slice(0, 5).map((s, i, arr) => (
                <Box
                  key={s.historyId ?? i}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: i < arr.length - 1 ? `1px solid ${theme.colors.borderDefault}` : 'none',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>{activityRoomLabel(s)}</Typography>
                    <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>{s.date} · {s.time}</Typography>
                  </Box>
                  <Chip label={s.duration} size="small" sx={{ bgcolor: theme.colors.bgSecondary, fontWeight: 600, fontSize: 12 }} />
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Current Plan + Overdue Rooms */}
        <Grid item xs={12} md={5}>
          {!isPurchaseUser && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: `${theme.radius.lg}px`, border: `1px solid ${theme.colors.borderDefault}`, mb: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 1.5 }}>Current Plan</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>Tier</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.colors.brandPrimary }}>{currentPlan.tier || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>Renewal</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>{currentPlan.renewalDate || '—'}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>Hours used</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.colors.textPrimary }}>
                    {currentPlanHoursCeiling != null ? `${currentPlanHoursUsed}h / ${currentPlanHoursCeiling}h` : '—'}
                  </Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: theme.colors.bgSecondary, borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(100, currentPlanUsageRatio * 100)}%`,
                      bgcolor: currentPlanUsageRatio > 0.9 ? theme.colors.accentDanger : theme.colors.brandPrimary,
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          )}

          {/* Overdue Rooms Banner */}
          {SHOW_ATTENTION_NEEDED && overdueRooms.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: `${theme.radius.lg}px`,
                bgcolor: conditionBg('warning'),
                border: `1px solid ${conditionColor('warning')}30`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MdWarning size={18} color={conditionColor('warning')} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: conditionColor('warning') }}>Attention Needed</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: theme.colors.textPrimary, mb: 1 }}>
                {overdueRooms.length} room(s) overdue for disinfection (&gt;14 days)
                {overdueRooms.length > 6 ? ` — showing latest 6` : ''}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {overdueRoomsAttention.map((r, i) => (
                  <Chip key={i} label={r.name} size="small" sx={{ bgcolor: conditionColor('warning'), color: '#fff', fontWeight: 600, fontSize: 11 }} />
                ))}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}
